import React, { useState, useCallback, useMemo, useEffect } from 'react';
import Form, { ButtonItem, GroupItem, RequiredRule, SimpleItem } from 'devextreme-react/form';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { Template } from 'devextreme-react/core/template';
import { formatMessage } from 'devextreme/localization';
import * as dayjs from 'dayjs';
import style from './auth.module.scss';
import { compare, getLocale, themeDark } from '../../utils/functions';
import PopupErrorMVP from '../../components/PopupErrorMVP';
// eslint-disable-next-line import/no-absolute-path
/* import AppStore from '/images/AppStore.svg';
import GooglePlay from '/images/GooglePlay.svg'; */

const cursor = {
  Username: null,
  Password: null
};

const buttonOptions = {
  text: formatMessage('login'),
  type: 'default',
  width: 120,
  height: 44,
  useSubmitBehavior: true,
  template: 'buttonItemLoad'
};

const initialErrorModal = {
  visibleError: false,
  message: '',
  techInfo: `${formatMessage('textSupportPhone')} 8-800-200-69-39`
};

const Auth = ({
  history,
  auth,
  userGetInfo,
  userCorporate,
  userGetInfoClear,
  authReducer: {
    error,
    loading,
    urlWebSSO
  },
  userReducer: {
    corporate,
    userInfo,
    userInfoError,
    userInfoStatus
  },
  getUrlWebSSOLogin,
  getUrlWebSSOLogout
}) => {
  const [inputPasswordMode, setInputPasswordMode] = useState({
    mode: 'password',
    icon: '/images/password-not-visible.svg'
  });
  const [isRedirect, setIsRedirect] = useState(false);
  const [errorModal, setErrorModal] = useState(initialErrorModal);

  useEffect(() => {
    userCorporate();
    userGetInfo();
    getUrlWebSSOLogin();
    getUrlWebSSOLogout();
    if (!!localStorage.getItem('redirectTime')
      && compare(dayjs(localStorage.getItem('redirectTime')).add(5, 'minute'), dayjs(new Date()
        .toLocaleString('en-US'))
        .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'))) {
      setIsRedirect(true);
      localStorage.removeItem('redirectTime');
    }
  }, [
    getUrlWebSSOLogin,
    getUrlWebSSOLogout,
    userCorporate,
    userGetInfo
  ]);

  const errorText = useCallback((data, itemElement) => {
    try {
      if (!!error && error.message) {
        // eslint-disable-next-line no-param-reassign
        itemElement.innerHTML = error.message;
      } else {
        // eslint-disable-next-line no-param-reassign
        itemElement.innerHTML = formatMessage('textIncorrect');
      }
    } catch (e) {
      console.debug(e);
    }
    return itemElement;
  }, [error]);

  const userText = useCallback((data, itemElement) => {
    try {
      // eslint-disable-next-line no-param-reassign
      itemElement.innerHTML = `${formatMessage('welcome')},<br />${userInfo.fullname}`;
    } catch (e) {
      console.debug(e);
    }
    return itemElement;
  }, [userInfo]);

  const onClickIconPassword = useCallback(() => {
    if (inputPasswordMode.mode === 'password') {
      setInputPasswordMode({
        mode: 'text',
        icon: '/images/password-visible.svg'
      });
    } else {
      setInputPasswordMode({
        mode: 'password',
        icon: '/images/password-not-visible.svg'
      });
    }
  }, [inputPasswordMode.mode]);

  const formOnSubmit = useCallback((e) => {
    e.preventDefault();
    auth(cursor, history);
  }, [auth, history]);

  const renderButton = useMemo(() => () => (
    <>
      <LoadIndicator className={style.indicator} visible={loading} />
      <span>{formatMessage('login')}</span>
    </>
  ), [loading]);

  const RenderForm = useCallback(() => (
    <form method="POST"
          action="auth"
          onSubmit={formOnSubmit}
    >
      <Form labelLocation="top"
            width={312}
            formData={cursor}
            readOnly={false}
            showColonAfterLabel
            showValidationSummary={false}
            elementAttr={{ class: style.devFrom }}>
        <GroupItem>
          <SimpleItem cssClass={style.formItemInput}
                      dataField="Username"
                      editorType="dxTextBox"
                      label={{ text: formatMessage('name') }}
          >
            <RequiredRule message={formatMessage('textErrorName')} />
          </SimpleItem>
          <SimpleItem cssClass={style.formItemInput}
                      dataField="Password"
                      editorType="dxTextBox"
                      label={{ text: formatMessage('password') }}
                      stylingMode="filled"
                      editorOptions={{
                        mode: inputPasswordMode.mode,
                        buttons: [{
                          name: 'trash',
                          location: 'after',
                          options: {
                            stylingMode: 'text',
                            icon: inputPasswordMode.icon,
                            onClick: onClickIconPassword
                          }
                        }]
                      }}
          >
            <RequiredRule message={formatMessage('textErrorPassword')} />
          </SimpleItem>
          <SimpleItem cssClass={style.errorText} template={errorText} visible={!!error} />
          <ButtonItem cssClass={style.buttonSubmit} horizontalAlignment="left"
                      buttonOptions={buttonOptions} />
          <Template name="buttonItemLoad" render={renderButton} />
        </GroupItem>
      </Form>
    </form>
  ), [
    error,
    errorText,
    formOnSubmit,
    inputPasswordMode.icon,
    inputPasswordMode.mode,
    onClickIconPassword,
    renderButton
  ]);

  const onSubmitInfoUser = useCallback((e) => {
    localStorage.setItem('authDateTime', new Date().toISOString());
    e.preventDefault();
    history.push('/stock');
  }, [history]);

  const RenderInfoUser = () => (
    <form method="POST"
          action="auth"
          onSubmit={onSubmitInfoUser}
    >
      <Form labelLocation="top"
            width={312}
            formData={cursor}
            readOnly={false}
            showColonAfterLabel
            showValidationSummary={false}
            elementAttr={{ class: `${style.devFrom} ${style.devFromInfo}` }}>
        <GroupItem>
          <SimpleItem cssClass={style.infoText} template={userText} visible />
          <ButtonItem cssClass={style.buttonSubmit} horizontalAlignment="left"
                      buttonOptions={buttonOptions} />
          <Template name="buttonItemLoad" render={renderButton} />
        </GroupItem>
      </Form>
    </form>
  );

  const onSubmitWebSSO = useCallback((e) => {
    e.preventDefault();
    document.location.href = `${document.location.origin}/api${urlWebSSO.login}`;
  }, [urlWebSSO.login]);

  const RenderWebSSO = useCallback(() => (
    <form method="GET"
          action={`/api${urlWebSSO.login}`}
          onSubmit={onSubmitWebSSO}
    >
      <Form labelLocation="top"
            width={312}
            formData={cursor}
            readOnly={false}
            showColonAfterLabel
            showValidationSummary={false}
            elementAttr={{ class: `${style.devFrom} ${style.devFromInfo}` }}>
        <GroupItem>
          <ButtonItem cssClass={style.buttonSubmit}
                      horizontalAlignment="left"
                      buttonOptions={buttonOptions}
          />
          <Template name={'buttonItemLoad'} render={renderButton} />
        </GroupItem>
      </Form>
    </form>
  ), [onSubmitWebSSO, renderButton, urlWebSSO.login]);

  const getOnHiding = useCallback(() => {
    userGetInfoClear();
    setErrorModal(initialErrorModal);
  }, [userGetInfoClear]);

  /** обработчик ошибок сервера */
  useEffect(() => {
    if (userInfoError) {
      let visibleError = false;
      let message = '';
      let techInfo = `${formatMessage('textSupportPhone')} 8-800-200-69-39`;
      switch (userInfoStatus) {
        case 401:
        case 403:
          if (userInfoError.message) { // если ошибка конец сессии и неизвестный пользователь
            visibleError = true;
            message = userInfoError.message;
          } else if (isRedirect) { // если был редирект с других страниц МЕСУС
            visibleError = true;
            message = formatMessage('textError').endSession;
          }
          break;
        case 500:
          visibleError = true;
          message = formatMessage('textError').cannotProcess;
          break;
        case 503:
          visibleError = true;
          message = formatMessage('textError').serviceUnavailable;
          break;
        case 504:
          visibleError = true;
          message = formatMessage('textError').gatewayTimeout;
          break;
        default:
          visibleError = true;
          if (userInfoError.message) {
            message = userInfoError.message;
          }
          message = formatMessage('textError').unknownError;
          if (userInfoError.techInfo) {
            techInfo = userInfoError.techInfo;
          }
      }
      setErrorModal({
        visibleError,
        message,
        techInfo
      });
    }
  }, [isRedirect, userInfoError, userInfoStatus]);

  return (
    <div className={style.containing}>
      <div className={style.containingFon} />
      <div className={style.containingForm}>
        <div className={style.formItems}>
          <img
            src={themeDark() ? `/images/${getLocale()}/severstal_logo_dark.png`
              : `/images/${getLocale()}/severstal_logo.png`}
            alt={formatMessage('severstalInfocom')} />
          <p>{formatMessage('textTitleAuth')}</p>
          { corporate && !userInfo ? <RenderForm /> : '' }
          { (corporate && userInfo) || (corporate === false && userInfo) ? <RenderInfoUser /> : '' }
          { corporate === false && !userInfo ? <RenderWebSSO /> : '' }
          {/* <div className={style.divQRCode}> */}
          {/*   <a href={'https://apps.rustore.ru/app/com.mesus.android '}> */}
          {/*     <img src={'/images/RuStore.svg'} className={style.objectQRCode} alt={formatMessage('RuStore')} /> */}
          {/*   </a> */}
          {/* </div> */}
          {/* <div className={style.invite}> */}
          {/*   /!* eslint-disable-next-line max-len *!/ */}
          {/*   <span>При регистрации в приложении потребуется ввести сначала одноразовый код из СМС и затем пригласительный код M2E58SUS</span> */}
          {/* </div> */}
          <span>{`${formatMessage('textSupportPhone')} 8-800-200-69-39`}</span>
        </div>
      </div>
      <PopupErrorMVP visible={errorModal.visibleError}
                     message={errorModal.message}
                     techInfo={errorModal.techInfo}
                     onHiding={getOnHiding}
                     types="info"
                     width={'600'}
      />
    </div>
  );
};

export default Auth;
